/**
 * Philippa — „The Witcher: Realms Fall".
 *
 * TWO STEPS AHEAD: na konci ťahu si smie dotiahnuť ruku do 4 kariet (ak ich
 * má 4 a viac, schopnosť sa nedá použiť). Veľa kariet núti súpera vybrať si
 * medzi odhodením karty a trestom.
 * (Realms Fall, „Philippa: Two Steps Ahead")
 */

import { shareZone, zonesOf } from '../map.js';
import type { Ctx } from '../ops.js';
import {
  askChoice,
  cardDef,
  dealDamage,
  discardFromHand,
  drawCard,
  emit,
  endTurnNow,
  getMap,
  getPlayer,
  setHealth,
} from '../ops.js';
import { areEnemies } from '../players.js';
import type { CardId, PlayerIndex } from '../types.js';
import { fighterOrNull, opponentOf, revealHand } from './helpers.js';
import { paralyzingFetters } from './yennefer.js';
import type { CardEffect, EffectRegistry, HeroSpecial } from './types.js';

const PHILIPPA = 'philippa';
const HAND_GOAL = 4;
const POLYMORPH_MOVE = 5;
const RUSE = 'philippa.spymasters-ruse';
const OWLFORM = 'philippa.owlform';

const philippaOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === PHILIPPA) ?? null;

function enemiesInZone(ctx: Ctx, player: PlayerIndex, zone: string): string[] {
  const map = getMap(ctx);
  return ctx.state.fighters
    .filter((f) => areEnemies(ctx.state, f.owner, player) && zonesOf(map, f.spaceId).includes(zone))
    .map((f) => f.id);
}

/** „Choose one: set Philippa's health to 5; draw 3 cards." */
const backupPlan: CardEffect = {
  scheme: (e) => {
    const follow = (data: string) => ({ kind: 'effect' as const, effect: 'philippa.backup-plan', data });
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: 'Backup Plan: vyber si jedno',
      optional: false,
      options: [
        { label: 'Philippa bude mať presne 5 zdravia', follow: follow('health') },
        { label: 'Potiahnuť 3 karty', follow: follow('draw') },
      ],
    });
  },
  onFollow: (ctx, choice, data) => {
    if (data === 'draw') {
      for (let i = 0; i < 3; i++) drawCard(ctx, choice.player);
      return;
    }
    const philippa = philippaOf(ctx, choice.player);
    if (philippa) setHealth(ctx, philippa.id, 5);
  },
};

/**
 * „Choose an opponent. They reveal 4 cards from their hand. Choose 2 of those
 * cards for them to discard. Then, they draw 2 cards."
 * Ktoré 4 ukáže, vyberá súper (po jednej); keď má 4 a menej, ukáže všetky.
 */
function askReveal(ctx: Ctx, opponent: PlayerIndex, source: string, revealed: CardId[]): void {
  askChoice(ctx, {
    kind: 'card',
    player: opponent,
    source,
    label: `Spymaster's Ruse: ukáž kartu z ruky (${revealed.length + 1} z 4)`,
    optional: false,
    from: 'hand',
    purpose: 'effect',
    except: revealed,
    cards: revealed,
  });
}

function afterReveal(ctx: Ctx, chooser: PlayerIndex, opponent: PlayerIndex, source: string, revealed: CardId[]): void {
  const picks = Math.min(2, revealed.length);
  for (let i = 0; i < picks; i++) {
    askChoice(ctx, {
      kind: 'card',
      player: chooser,
      source,
      label: `Spymaster's Ruse: vyber kartu, ktorú súper odhodí (${i + 1} z ${picks})`,
      optional: false,
      from: 'hand',
      owner: opponent,
      purpose: 'discard',
      only: revealed,
    });
  }
  askChoice(ctx, {
    kind: 'auto',
    player: chooser,
    source,
    label: "Spymaster's Ruse: súper ťahá 2",
    optional: false,
    follow: { kind: 'effect', effect: RUSE, data: `draw:${opponent}` },
  });
}

const spymastersRuse: CardEffect = {
  scheme: (e) => {
    const opponent = opponentOf(e);
    if (opponent === null) return;
    const hand = getPlayer(e.ctx.state, opponent).hand;
    if (hand.length <= HAND_GOAL) {
      for (const cardId of hand) emit(e.ctx, { type: 'cardRevealed', player: opponent, cardId });
      afterReveal(e.ctx, e.player, opponent, e.cardId, hand.slice());
      return;
    }
    askReveal(e.ctx, opponent, e.cardId, []);
  },
  onCardChosen: (ctx, choice, cardId) => {
    if (!choice.source) return;
    const owner = ctx.state.cards[choice.source]!.owner;
    emit(ctx, { type: 'cardRevealed', player: choice.player, cardId });
    const revealed = [...(choice.cards ?? []), cardId];
    if (revealed.length < HAND_GOAL) askReveal(ctx, choice.player, choice.source, revealed);
    else afterReveal(ctx, owner, choice.player, choice.source, revealed);
  },
  onFollow: (ctx, _choice, data) => {
    const opponent = Number(data.split(':')[1]);
    drawCard(ctx, opponent);
    drawCard(ctx, opponent);
  },
};

/**
 * „Place Philippa in any space. ONGOING: Philippa's move value is 5. Discard
 * this card at the end of your turn if Philippa is not in a zone with an
 * opposing fighter." Pohyb sa po skončení schémy vráti na hodnotu z karty postavy.
 */
const polymorphy: CardEffect = {
  scheme: (e) => {
    const philippa = philippaOf(e.ctx, e.player);
    if (!philippa) return;
    philippa.move = POLYMORPH_MOVE;
    askChoice(e.ctx, {
      kind: 'place',
      player: e.player,
      source: e.cardId,
      label: 'Polymorphy: polož Philippu na ľubovoľný priestor',
      optional: false,
      fighterId: philippa.id,
      spaces: getMap(e.ctx).spaces.map((s) => s.id).filter((id) => !e.ctx.state.fighters.some((f) => f.spaceId === id)),
    });
  },
  ongoing: {
    discardAtEnd: (ctx, player) => {
      const philippa = philippaOf(ctx, player);
      if (!philippa) return true;
      const map = getMap(ctx);
      return !ctx.state.fighters.some(
        (f) => areEnemies(ctx.state, f.owner, player) && shareZone(map, f.spaceId, philippa.spaceId),
      );
    },
    onEnd: (ctx, player) => {
      const philippa = philippaOf(ctx, player);
      const base = ctx.content.heroes[PHILIPPA]?.move ?? 2;
      if (philippa) philippa.move = base;
    },
  },
};

/**
 * „IMMEDIATELY: Return your opponent's attack card to their hand. Look at their
 * hand and choose an attack or versatile card for them to play. (It may be the
 * same card.)" Nová karta dostane svoj IMMEDIATELY až v kroku útočníka.
 */
export const doMyBidding: CardEffect = {
  immediate: (e) => {
    const combat = e.ctx.state.combat;
    if (e.role !== 'defender' || !combat || combat.bonus || e.opponentPlayer === null) return;
    // A Magician Never Reveals His Secrets nazretie zruší — potom sa nič nemení.
    if (!revealHand(e, e.opponentPlayer)) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'Do My Bidding: vyber súperovi útočnú kartu, ktorú zahrá (aj tú istú)',
      optional: false,
      from: 'hand',
      owner: e.opponentPlayer,
      purpose: 'replace',
      playableBy: combat.attackerFighter,
    });
  },
};

/** „You may discard 1 card. If you do, deal damage to the opposing fighter equal to its BOOST value." */
const blindingDust: CardEffect = {
  after: (e) => {
    if ((e.ctx.state.players[e.player]?.hand.length ?? 0) === 0 || !e.opponentFighterId) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'Blinding Dust: smieš odhodiť kartu — súper dostane poškodenie ako jej boost',
      optional: true,
      from: 'hand',
      purpose: 'effect',
      discards: true,
      // Po súboji už `combat` nie je — cieľ nesie voľba.
      target: e.opponentFighterId,
    });
  },
  onCardChosen: (ctx, choice, cardId) => {
    discardFromHand(ctx, choice.player, cardId, 'effect');
    if (choice.target) dealDamage(ctx, choice.target, cardDef(ctx, cardId).boost);
  },
};

/**
 * „Your opponent may discard 1 card. If they don't, deal 2 damage to the
 * opposing fighter." — „opposing fighter" z pohľadu hráča karty, teda súperova figúrka.
 */
const cunning: CardEffect = {
  after: (e) => {
    const opponent = e.opponentPlayer;
    const target = e.opponentFighterId;
    if (opponent === null || !target) return;
    if ((e.ctx.state.players[opponent]?.hand.length ?? 0) === 0) {
      dealDamage(e.ctx, target, 2);
      return;
    }
    askChoice(e.ctx, {
      kind: 'confirm',
      player: opponent,
      source: e.cardId,
      label: 'Cunning: odhodíš 1 kartu? Ak nie, tvoja figúrka dostane 2 poškodenia.',
      optional: true,
      effect: 'philippa.cunning',
      yes: 'Odhodiť 1 kartu',
      no: 'Nie — 2 poškodenia',
      onSkip: { kind: 'effect', effect: 'philippa.cunning', data: target },
    });
  },
  onConfirm: (ctx, choice) => askOwnDiscard(ctx, choice.player, choice.source, 'Cunning: odhoď 1 kartu'),
  onCardChosen: (ctx, choice, cardId) => discardFromHand(ctx, choice.player, cardId, 'effect'),
  onFollow: (ctx, _choice, target) => dealDamage(ctx, target, 2),
};

/** „IMMEDIATELY: Your opponent may discard 1 card. If they don't, ignore their card's value." */
const redanianPlot: CardEffect = {
  immediate: (e) => {
    const combat = e.ctx.state.combat;
    const opponent = e.opponentPlayer;
    if (!combat || opponent === null) return;
    const flag = e.role === 'attacker' ? 'zeroDefense' : 'zeroAttack';
    if ((e.ctx.state.players[opponent]?.hand.length ?? 0) === 0) {
      (combat.flags ??= []).push(flag);
      return;
    }
    askChoice(e.ctx, {
      kind: 'confirm',
      player: opponent,
      source: e.cardId,
      label: 'Redanian Plot: odhodíš 1 kartu? Ak nie, hodnota tvojej karty sa ignoruje.',
      optional: true,
      effect: 'philippa.redanian-plot',
      yes: 'Odhodiť 1 kartu',
      no: 'Nie — ignorovať hodnotu',
      onSkip: { kind: 'effect', effect: 'philippa.redanian-plot', data: flag },
    });
  },
  onConfirm: (ctx, choice) => askOwnDiscard(ctx, choice.player, choice.source, 'Redanian Plot: odhoď 1 kartu'),
  onCardChosen: (ctx, choice, cardId) => discardFromHand(ctx, choice.player, cardId, 'effect'),
  onFollow: (ctx, _choice, flag) => {
    const combat = ctx.state.combat;
    if (combat) (combat.flags ??= []).push(flag);
  },
};

function askOwnDiscard(ctx: Ctx, player: PlayerIndex, source: string | null, label: string): void {
  askChoice(ctx, { kind: 'card', player, source, label, optional: false, from: 'hand', purpose: 'effect', discards: true });
}

/** „IMMEDIATELY: Cancel all effects on your opponent's card. AFTER COMBAT: End the turn." */
const spellbreaker: CardEffect = {
  cancelsOpponent: true,
  after: (e) => endTurnNow(e.ctx),
};

/** „Choose a zone Philippa is in. Deal 1 damage to each opposing fighter in that zone." */
const chainLightning: CardEffect = {
  after: (e) => {
    const philippa = philippaOf(e.ctx, e.player);
    if (!philippa) return;
    const zones = zonesOf(getMap(e.ctx), philippa.spaceId);
    if (zones.length === 1) {
      for (const id of enemiesInZone(e.ctx, e.player, zones[0]!)) dealDamage(e.ctx, id, 1);
      return;
    }
    const map = getMap(e.ctx);
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: 'Chain Lightning: vyber zónu, v ktorej Philippa stojí',
      optional: false,
      options: zones.map((zone) => ({
        label: map.zones.find((z) => z.id === zone)?.name ?? zone,
        follow: { kind: 'effect' as const, effect: 'philippa.chain-lightning', data: zone },
      })),
    });
  },
  onFollow: (ctx, choice, zone) => {
    for (const id of enemiesInZone(ctx, choice.player, zone)) dealDamage(ctx, id, 1);
  },
};

/** „If the opposing fighter is a hero, this card's value is a 6 instead." */
const regicide: CardEffect = {
  during: (e, value) => (fighterOrNull(e.ctx, e.opponentFighterId)?.kind === 'hero' ? 6 : value),
};

/** „If you haven't taken a maneuver action this turn, deal 1 damage to each opposing fighter in Philippa's zone." */
const lightningBolt: CardEffect = {
  after: (e) => {
    if (e.ctx.state.turn.actionsUsed.includes('maneuver')) return;
    const philippa = philippaOf(e.ctx, e.player);
    if (!philippa) return;
    const map = getMap(e.ctx);
    const targets = e.ctx.state.fighters
      .filter((f) => areEnemies(e.ctx.state, f.owner, e.player) && shareZone(map, f.spaceId, philippa.spaceId))
      .map((f) => f.id);
    for (const id of targets) dealDamage(e.ctx, id, 1);
  },
};

/** „DURING COMBAT: Discard any number of cards from your hand. Add +1 to this card's value for each card discarded." */
function askOwl(ctx: Ctx, player: PlayerIndex, source: string, side: 'attacker' | 'defender'): void {
  if ((ctx.state.players[player]?.hand.length ?? 0) === 0) return;
  askChoice(ctx, {
    kind: 'card',
    player,
    source,
    label: 'Owlform: odhoď kartu za +1 (koľko chceš)',
    optional: true,
    from: 'hand',
    purpose: 'effect',
    discards: true,
    side,
  });
}

const owlform: CardEffect = {
  duringChoice: (e) => askOwl(e.ctx, e.player, e.cardId, e.role === 'defender' ? 'defender' : 'attacker'),
  onCardChosen: (ctx, choice, cardId) => {
    const combat = ctx.state.combat;
    if (!combat || !choice.source) return;
    discardFromHand(ctx, choice.player, cardId, 'effect');
    const side = choice.side ?? 'attacker';
    combat.boosts ??= { attacker: 0, defender: 0 };
    combat.boosts[side] += 1;
    askOwl(ctx, choice.player, choice.source, side);
  },
};

export const philippaCards: EffectRegistry = {
  'philippa.backup-plan': backupPlan,
  [RUSE]: spymastersRuse,
  'philippa.polymorphy': polymorphy,
  'philippa.paralyzing-fetters': paralyzingFetters,
  'philippa.do-my-bidding': doMyBidding,
  'philippa.blinding-dust': blindingDust,
  'philippa.cunning': cunning,
  'philippa.redanian-plot': redanianPlot,
  'philippa.spellbreaker': spellbreaker,
  'philippa.chain-lightning': chainLightning,
  'philippa.regicide': regicide,
  'philippa.lightning-bolt': lightningBolt,
  [OWLFORM]: owlform,
};

/** TWO STEPS AHEAD — „At the end of your turn, you may draw until you have a hand of 4 cards." */
export const philippaSpecial: HeroSpecial = {
  name: 'Two Steps Ahead',
  endOfTurn: (ctx, player) => {
    if ((ctx.state.players[player]?.hand.length ?? 0) >= HAND_GOAL) return;
    askChoice(ctx, {
      kind: 'confirm',
      player,
      source: null,
      label: 'Two Steps Ahead: dotiahnuť ruku do 4 kariet?',
      optional: true,
      special: PHILIPPA,
      yes: 'Dotiahnuť',
      no: 'Nie',
    });
  },
  onConfirm: (ctx, player) => {
    while ((ctx.state.players[player]?.hand.length ?? HAND_GOAL) < HAND_GOAL) {
      if (drawCard(ctx, player) === null) break;
    }
  },
};
